// Bubble System for SimpleGame - Modular and Extensible

export enum BubbleType {
  DOLLAR_BONUS = "DOLLAR_BONUS",
}

export interface Bubble {
  id: string;
  type: BubbleType;
  x: number;
  y: number;
  value: number;
  size: number;
  speed: number;
  floatAmplitude: number;
  floatPhase: number;
  createdAt: number;
}

export interface BubbleReward {
  type: BubbleType;
  value: number;
  x: number;
  y: number;
}

export interface BubbleConfig {
  // Spawn settings
  spawnRate: number; // Bubbles per second
  spawnChance: number; // 0-1 probability per frame

  // Movement settings
  baseSpeed: number; // Pixels per frame
  speedVariation: number; // ±variation in speed

  // Float animation
  floatAmplitude: number; // Vertical float range
  floatSpeed: number; // How fast the floating motion is

  // Bubble appearance
  minSize: number;
  maxSize: number;

  // Bonus values (as percentages of initial money)
  minPercentValue: number; // Minimum percentage (e.g., 1 = 1%)
  maxPercentValue: number; // Maximum percentage (e.g., 20 = 20%)

  // Collision
  collisionTolerance: number; // Distance for line collision
}

export const DEFAULT_BUBBLE_CONFIG: BubbleConfig = {
  spawnRate: 0.005,
  spawnChance: 0.004, // Approximately matches spawn rate at 60fps

  baseSpeed: 7,
  speedVariation: 1.5,

  floatAmplitude: 0.5,
  floatSpeed: 0.02,

  minSize: 25,
  maxSize: 40,

  minPercentValue: 1, // 0.5% of initial money
  maxPercentValue: 5, // 5% of initial money

  collisionTolerance: 15,
};

export class BubbleSystem {
  private bubbles: Bubble[] = [];
  private config: BubbleConfig;
  private nextId = 0;
  private time = 0;
  private initialMoney: number = 1000; // Default value

  constructor(config: BubbleConfig = DEFAULT_BUBBLE_CONFIG) {
    this.config = { ...config };
  }

  updateConfig(newConfig: Partial<BubbleConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  setInitialMoney(amount: number): void {
    this.initialMoney = amount;
  }

  update(canvasWidth: number, canvasHeight: number): void {
    this.time++;

    // Spawn new bubbles
    //this.spawnBubbles(canvasWidth, canvasHeight);

    // Update existing bubbles
   // this.updateBubbles();

    // Remove bubbles that have moved off-screen
    this.cleanupBubbles();
  }

  private spawnBubbles(canvasWidth: number, canvasHeight: number): void {
    if (Math.random() < this.config.spawnChance) {
      const bubble = this.createDollarBubble(canvasWidth, canvasHeight);
      this.bubbles.push(bubble);
    }
  }

  private createDollarBubble(
    canvasWidth: number,
    canvasHeight: number
  ): Bubble {
    const speed =
      this.config.baseSpeed +
      (Math.random() - 0.5) * 2 * this.config.speedVariation;

    // Calculate percentage value (1% to 20% of initial money)
    const percentValue =
      this.config.minPercentValue +
      Math.random() *
        (this.config.maxPercentValue - this.config.minPercentValue);

    // Calculate dollar value based on percentage
    const value = Math.floor((percentValue / 100) * this.initialMoney);

    // Calculate size based on percentage (larger percentage = larger bubble)
    const percentProgress =
      (percentValue - this.config.minPercentValue) /
      (this.config.maxPercentValue - this.config.minPercentValue);

    const size =
      this.config.minSize +
      percentProgress * (this.config.maxSize - this.config.minSize);

    return {
      id: `bubble_${this.nextId++}`,
      type: BubbleType.DOLLAR_BONUS,
      x: canvasWidth + size, // Start just off-screen to the right
      y: 100 + Math.random() * (canvasHeight - 200), // Random height with margins
      value,
      size,
      speed,
      floatAmplitude: this.config.floatAmplitude,
      floatPhase: Math.random() * Math.PI * 2, // Random starting phase
      createdAt: this.time,
    };
  }

  private updateBubbles(): void {
    this.bubbles.forEach((bubble) => {
      // Move left
      bubble.x -= bubble.speed;

      // Apply floating motion
      const floatOffset =
        Math.sin(
          (this.time - bubble.createdAt) * this.config.floatSpeed +
            bubble.floatPhase
        ) * bubble.floatAmplitude;

      // Store original Y for collision detection (without float offset)
      const baseY = bubble.y;
      bubble.y = baseY + floatOffset;
    });
  }

  private cleanupBubbles(): void {
    this.bubbles = this.bubbles.filter(
      (bubble) => bubble.x + bubble.size > -50
    );
  }

  collectBubbleAtPoint(x: number, y: number): BubbleReward[] {
    const rewards: BubbleReward[] = [];

    this.bubbles = this.bubbles.filter((bubble) => {
      const dx = bubble.x - x;
      const dy = bubble.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const hitRadius = bubble.size + this.config.collisionTolerance;

      if (distance <= hitRadius) {
        rewards.push({
          type: bubble.type,
          value: bubble.value,
          x: bubble.x,
          y: bubble.y,
        });
        return false; // Remove collected bubble
      }

      return true;
    });

    return rewards;
  }

  checkCollisionWithLine(
    lineStartX: number,
    lineStartY: number,
    lineEndX: number,
    lineEndY: number
  ): BubbleReward[] {
    const rewards: BubbleReward[] = [];

    this.bubbles = this.bubbles.filter((bubble) => {
      const collision = this.isPointNearLine(
        bubble.x,
        bubble.y,
        bubble.size,
        lineStartX,
        lineStartY,
        lineEndX,
        lineEndY
      );

      if (collision) {
        rewards.push({
          type: bubble.type,
          value: bubble.value,
          x: bubble.x,
          y: bubble.y,
        });
        return false; // Remove bubble
      }

      return true; // Keep bubble
    });

    return rewards;
  }

  private isPointNearLine(
    pointX: number,
    pointY: number,
    pointRadius: number,
    lineStartX: number,
    lineStartY: number,
    lineEndX: number,
    lineEndY: number
  ): boolean {
    // Calculate distance from point to line segment
    const A = pointX - lineStartX;
    const B = pointY - lineStartY;
    const C = lineEndX - lineStartX;
    const D = lineEndY - lineStartY;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;

    if (lenSq === 0) {
      // Line segment is a point
      const distance = Math.sqrt(A * A + B * B);
      return distance <= pointRadius + this.config.collisionTolerance;
    }

    let param = dot / lenSq;

    let xx, yy;

    if (param < 0) {
      xx = lineStartX;
      yy = lineStartY;
    } else if (param > 1) {
      xx = lineEndX;
      yy = lineEndY;
    } else {
      xx = lineStartX + param * C;
      yy = lineStartY + param * D;
    }

    const dx = pointX - xx;
    const dy = pointY - yy;
    const distance = Math.sqrt(dx * dx + dy * dy);

    return distance <= pointRadius + this.config.collisionTolerance;
  }

  draw(ctx: CanvasRenderingContext2D): void {
    this.bubbles.forEach((bubble) => {
      this.drawBubble(ctx, bubble);
    });
  }

  private drawBubble(ctx: CanvasRenderingContext2D, bubble: Bubble): void {
    const { x, y, size, type, value } = bubble;

    // Save context
    ctx.save();

    // Draw bubble background
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, size);

    if (type === BubbleType.DOLLAR_BONUS) {
      gradient.addColorStop(0, "rgba(255, 215, 0, 0.9)"); // Gold center
      gradient.addColorStop(0.7, "rgba(255, 215, 0, 0.6)");
      gradient.addColorStop(1, "rgba(255, 215, 0, 0.3)"); // Transparent edge
    }

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();

    // Draw bubble border
    ctx.strokeStyle = "rgba(255, 255, 255, 0.8)";
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw bubble highlight
    ctx.fillStyle = "rgba(255, 255, 255, 0.4)";
    ctx.beginPath();
    ctx.arc(x - size * 0.3, y - size * 0.3, size * 0.3, 0, Math.PI * 2);
    ctx.fill();

    // Draw value text
    ctx.fillStyle = "#000";
    ctx.font = "bold 12px monospace";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    if (type === BubbleType.DOLLAR_BONUS) {
      ctx.fillText(`$${value}`, x, y);
    }

    // Restore context
    ctx.restore();
  }

  getBubbles(): readonly Bubble[] {
    return this.bubbles;
  }

  clear(): void {
    this.bubbles = [];
    this.nextId = 0;
    this.time = 0;
    this.initialMoney = 1000; // Reset to default
  }
}
